import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface InventoryItem {
  id: number;
  sku: string;
  name: string;
  description: string;
  quantity: number;
  unit_cost: number;
  total_value: number;
  reorder_point: number;
  category: string;
  location?: string;
}

// Placeholder data
const inventory: InventoryItem[] = [
  { id: 1, sku: 'OFF-001', name: 'Office Paper (Ream)', description: '8.5x11 copy paper', quantity: 50, unit_cost: 8.99, total_value: 449.50, reorder_point: 20, category: 'Office Supplies', location: 'Storage A' },
  { id: 2, sku: 'TECH-001', name: 'USB-C Cable (1m)', description: 'USB-C to USB-C charging cable', quantity: 15, unit_cost: 12.50, total_value: 187.50, reorder_point: 10, category: 'Electronics', location: 'Storage B' },
  { id: 3, sku: 'TECH-002', name: 'Wireless Mouse', description: 'Logitech MX Master', quantity: 3, unit_cost: 99.00, total_value: 297.00, reorder_point: 5, category: 'Electronics', location: 'Storage B' },
  { id: 4, sku: 'OFF-002', name: 'Ballpoint Pens (Box)', description: 'Blue ink, 12-pack', quantity: 8, unit_cost: 15.99, total_value: 127.92, reorder_point: 5, category: 'Office Supplies', location: 'Storage A' },
  { id: 5, sku: 'FURN-001', name: 'Standing Desk Converter', description: 'Sit-stand desktop workstation', quantity: 2, unit_cost: 350.00, total_value: 700.00, reorder_point: 1, category: 'Furniture' },
];

const categories = [...new Set(inventory.map(i => i.category))];

export function Inventory() {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const totalValue = inventory.reduce((sum, i) => sum + i.total_value, 0);
  const lowStock = inventory.filter(i => i.quantity <= i.reorder_point);
  const itemCount = inventory.length;
  const totalUnits = inventory.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Inventory</h1>
          <p className="text-sm text-muted-foreground">Track physical inventory and supplies</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm">
            <i className="bi bi-barcode"></i> Scan
          </Button>
          <Button variant="default" size="sm">
            <i className="bi bi-plus"></i> Add Item
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Value</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">{formatCurrency(totalValue)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Items</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{itemCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Units</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUnits}</div>
          </CardContent>
        </Card>
        <Card className={lowStock.length > 0 ? 'border-[var(--accent)]' : ''}>
          <CardHeader className="pb-2">
            <CardDescription>Low Stock</CardDescription>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${lowStock.length > 0 ? 'text-[var(--accent)]' : ''}`}>
              {lowStock.length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low Stock Alert */}
      {lowStock.length > 0 && (
        <Card className="border-[var(--accent)] bg-[var(--accent-bg)]">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <i className="bi bi-exclamation-triangle text-[var(--accent)] text-xl mt-0.5"></i>
              <div className="flex-1">
                <p className="font-semibold text-foreground">{lowStock.length} items below reorder point</p>
                <p className="text-sm text-muted-foreground">Consider restocking these items to avoid shortages.</p>
              </div>
              <Button variant="outline" size="sm" className="border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent-bg)]">
                View Low Stock
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inventory List */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle>Inventory Items</CardTitle>
            <div className="flex gap-2">
              <select className="px-3 py-1.5 text-sm border border-input rounded-md bg-background">
                <option>All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <input type="text" className="px-3 py-1.5 text-sm border border-input rounded-md bg-background" placeholder="Search items..." />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">SKU</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Name</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Category</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Quantity</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Unit Cost</th>
                  <th className="py-3 px-4 text-right font-medium text-muted-foreground">Total Value</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Status</th>
                  <th className="py-3 px-4 text-left font-medium text-muted-foreground">Actions</th>
                </tr>
              </thead>
              <tbody>
                {inventory.map((item) => (
                  <tr key={item.id} className="border-b border-border hover:bg-muted/50">
                    <td className="py-3 px-4"><code className="text-xs bg-muted px-1.5 py-0.5 rounded">{item.sku}</code></td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col">
                        {item.name}
                        {item.location && (
                          <span className="text-xs text-muted-foreground"><i className="bi bi-geo-alt"></i> {item.location}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4">{item.category}</td>
                    <td className="py-3 px-4 text-right font-mono">{item.quantity}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatCurrency(item.unit_cost)}</td>
                    <td className="py-3 px-4 text-right font-mono">{formatCurrency(item.total_value)}</td>
                    <td className="py-3 px-4">
                      {item.quantity <= item.reorder_point ? (
                        <Badge variant="outline">Low Stock</Badge>
                      ) : (
                        <Badge variant="secondary">In Stock</Badge>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm">
                          <i className="bi bi-pencil"></i>
                        </Button>
                        <Button variant="ghost" size="sm">
                          <i className="bi bi-box-arrow-in-down"></i>
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Valuation Report */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-graph-up"></i> Inventory Valuation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Method:</span>
              <span className="font-mono">FIFO (First In, First Out)</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Valuation Date:</span>
              <span className="font-mono">{new Date().toLocaleDateString('en-CA')}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-border font-semibold">
              <span>Total Inventory Value:</span>
              <span className="font-mono">{formatCurrency(totalValue)}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}